/**
 * The OTel EXCEPTION semantic-convention attribute keys -- the ONE home for `exception.type` / `exception.message` /
 * `exception.stacktrace`, read by every emitter that stamps an exception onto a record.
 *
 * MINTED 2026-08-09 BY THE SECOND EMITTER, which is exactly the trigger the previous home declared. These three keys
 * lived in `const/worker-shell-diagnostics.ts`, whose docstring stated the condition verbatim: "The guard is also their
 * only emitter at HEAD; the moment a SECOND emitter appears this record is the wrong home and should move to a
 * dedicated `const/` leaf both emitters import -- do not copy it." The browser producer
 * (`const/browser-telemetry-attrs.ts`) was that second emitter and had already spelled all three itself, so the
 * vocabulary was live in two homes; this leaf is the move that condition asked for, with the shell leaf and the browser
 * leaf both READING from here.
 *
 * NO NAMESPACE PREFIX, DELIBERATELY. These are the STANDARD upstream spellings, not this repo's invention, so a reader
 * or collector that already understands OTel exceptions understands them with no mapping. That is also why they are NOT
 * members of `FEDERA_ATTRS` (`const/telemetry-federa-attrs.ts`): that registry is the `federa.`-NAMESPACED vocabulary
 * this repo authors and answers for, and enrolling an upstream convention there would claim authorship of a convention
 * this repo does not own -- the namespace arm of `QUAL-FEDERA-ATTR-KEY-RAW-LITERAL-001` keys on exactly that prefix.
 *
 * MEMBERSHIP RULE: one member per key the OTel exception convention defines AND a live emitter in this tree writes.
 * A member nothing emits is dead vocabulary; a key an emitter needs that the convention does not define is first-party
 * and belongs in the emitter's own namespaced record (`WORKER_GUARD_ATTRS.EXCEPTION_CHAIN` is the live example -- a
 * rendered cause chain is structurally not a stack, so it rides its own `federa.worker.*` key rather than being folded
 * onto `exception.stacktrace`).
 */

export const OTEL_EXCEPTION_ATTRS = {
	/** The exception class name (`Error.name`), or the JS `typeof` for a thrown value that is not an `Error`. */
	TYPE: 'exception.type',
	/** The exception message. */
	MESSAGE: 'exception.message',
	/** The captured stack frames (`Error.stack`); absent on a thrown value that is not an `Error`. */
	STACKTRACE: 'exception.stacktrace',
} as const satisfies Record<string, string>;
